/**
 * Typed bridge to @agent-maurice/ui.
 * Components are cast through `any` so React 18/19 @types/react dual-resolution
 * (file: linked package) does not erase DOM prop types at the call site.
 */
import * as React from 'react'
import {
  Badge as UiBadge,
  Button as UiButton,
  Card as UiCard,
  CardContent as UiCardContent,
  CardDescription as UiCardDescription,
  CardFooter as UiCardFooter,
  CardHeader as UiCardHeader,
  CardTitle as UiCardTitle,
  Dialog as UiDialog,
  DialogClose as UiDialogClose,
  DialogContent as UiDialogContent,
  DialogDescription as UiDialogDescription,
  DialogFooter as UiDialogFooter,
  DialogHeader as UiDialogHeader,
  DialogOverlay as UiDialogOverlay,
  DialogPortal as UiDialogPortal,
  DialogTitle as UiDialogTitle,
  DialogTrigger as UiDialogTrigger,
  Input as UiInput,
  Sheet as UiSheet,
  SheetClose as UiSheetClose,
  SheetContent as UiSheetContent,
  SheetDescription as UiSheetDescription,
  SheetFooter as UiSheetFooter,
  SheetHeader as UiSheetHeader,
  SheetOverlay as UiSheetOverlay,
  SheetPortal as UiSheetPortal,
  SheetTitle as UiSheetTitle,
  SheetTrigger as UiSheetTrigger,
  Tabs as UiTabs,
  TabsContent as UiTabsContent,
  TabsList as UiTabsList,
  TabsTrigger as UiTabsTrigger,
  badgeVariants,
  buttonVariants,
} from '@agent-maurice/ui'

export type ButtonProps = React.ComponentPropsWithoutRef<'button'> & {
  // Aurora (0.2.0): one `default` (--primary pill) per screen, `secondary` /
  // `outline` outlined pills, `destructive` text in --error. The deprecated
  // `success` / `warning` variants are not exposed to the OS.
  variant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'ghost' | 'link'
  size?: 'default' | 'sm' | 'lg' | 'icon'
  asChild?: boolean
}

export type InputProps = React.ComponentPropsWithoutRef<'input'>

export type BadgeProps = React.HTMLAttributes<HTMLDivElement> & {
  variant?:
    | 'default'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'destructive'
    | 'outline'
    | 'info'
    | 'muted'
}

export const Button = UiButton as unknown as React.ForwardRefExoticComponent<
  ButtonProps & React.RefAttributes<HTMLButtonElement>
>
export const Input = UiInput as unknown as React.ForwardRefExoticComponent<
  InputProps & React.RefAttributes<HTMLInputElement>
>
export const Badge = UiBadge as unknown as React.FC<BadgeProps>

export const Card = UiCard as unknown as React.FC<React.HTMLAttributes<HTMLDivElement>>
export const CardHeader = UiCardHeader as unknown as React.FC<React.HTMLAttributes<HTMLDivElement>>
export const CardFooter = UiCardFooter as unknown as React.FC<React.HTMLAttributes<HTMLDivElement>>
export const CardTitle = UiCardTitle as unknown as React.FC<React.HTMLAttributes<HTMLHeadingElement>>
export const CardDescription = UiCardDescription as unknown as React.FC<
  React.HTMLAttributes<HTMLParagraphElement>
>
export const CardContent = UiCardContent as unknown as React.FC<React.HTMLAttributes<HTMLDivElement>>

export const Dialog = UiDialog as unknown as React.FC<{
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  modal?: boolean
  children?: React.ReactNode
}>
export const DialogPortal = UiDialogPortal as any
export const DialogOverlay = UiDialogOverlay as any
export const DialogClose = UiDialogClose as any
export const DialogTrigger = UiDialogTrigger as any
// Aurora flat rendering (os-aurora-migration § 3): the shared Dialog/Sheet
// still ship a large drop shadow; the OS removes it (tailwind-merge keeps the last).
const FLAT_OVERLAY_CLASSNAME = 'shadow-none'
export const DialogContent = React.forwardRef<HTMLDivElement, { className?: string; [key: string]: unknown }>(
  function DialogContent({ className, ...props }, ref) {
    const Content = UiDialogContent as any
    return React.createElement(Content, { ref, className: [FLAT_OVERLAY_CLASSNAME, 'rounded-card', className].filter(Boolean).join(' '), ...props })
  },
) as any
export const DialogHeader = UiDialogHeader as any
export const DialogFooter = UiDialogFooter as any
export const DialogTitle = UiDialogTitle as any
export const DialogDescription = UiDialogDescription as any

export const Tabs = UiTabs as unknown as React.FC<{
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  orientation?: 'horizontal' | 'vertical'
  dir?: 'ltr' | 'rtl'
  activationMode?: 'automatic' | 'manual'
  children?: React.ReactNode
  className?: string
}>
// Aurora tabs: outlined pills in a bar, the active one filled with --primary.
const TABS_LIST_CLASSNAME = 'h-auto gap-2 rounded-none border-0 bg-transparent p-0'
const TABS_TRIGGER_CLASSNAME =
  'min-h-10 rounded-full border border-border bg-transparent px-4 py-2 font-semibold text-foreground hover:bg-card-raised data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none'
export const TabsList = React.forwardRef<HTMLDivElement, { className?: string; [key: string]: unknown }>(
  function TabsList({ className, ...props }, ref) {
    const List = UiTabsList as any
    return React.createElement(List, { ref, className: [TABS_LIST_CLASSNAME, className].filter(Boolean).join(' '), ...props })
  },
) as any
export const TabsTrigger = React.forwardRef<HTMLButtonElement, { className?: string; [key: string]: unknown }>(
  function TabsTrigger({ className, ...props }, ref) {
    const Trigger = UiTabsTrigger as any
    return React.createElement(Trigger, { ref, className: [TABS_TRIGGER_CLASSNAME, className].filter(Boolean).join(' '), ...props })
  },
) as any
export const TabsContent = UiTabsContent as any

export const Sheet = UiSheet as unknown as React.FC<{
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  modal?: boolean
  children?: React.ReactNode
}>
export const SheetPortal = UiSheetPortal as any
export const SheetOverlay = UiSheetOverlay as any
export const SheetTrigger = UiSheetTrigger as any
export const SheetClose = UiSheetClose as any
const SheetContentFlat = React.forwardRef<HTMLDivElement, { className?: string; [key: string]: unknown }>(
  function SheetContent({ className, ...props }, ref) {
    const Content = UiSheetContent as any
    return React.createElement(Content, { ref, className: [FLAT_OVERLAY_CLASSNAME, className].filter(Boolean).join(' '), ...props })
  },
)
export const SheetContent = SheetContentFlat as unknown as React.ForwardRefExoticComponent<
  React.ComponentPropsWithoutRef<'div'> & {
    side?: 'top' | 'bottom' | 'left' | 'right'
    closeLabel?: string
    className?: string
    children?: React.ReactNode
    onCloseAutoFocus?: (event: Event) => void
    onOpenAutoFocus?: (event: Event) => void
  } & React.RefAttributes<HTMLDivElement>
>
export const SheetHeader = UiSheetHeader as unknown as React.FC<
  React.HTMLAttributes<HTMLDivElement>
>
export const SheetFooter = UiSheetFooter as unknown as React.FC<
  React.HTMLAttributes<HTMLDivElement>
>
export const SheetDescription = UiSheetDescription as any
export const SheetTitle = UiSheetTitle as any

export { badgeVariants, buttonVariants }
